import { useAdminAccess } from "src/hooks/useAdminAccess";
import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { Field, Form, Formik, getIn, setIn } from "formik";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import InlineSVG from "react-inlinesvg";
import { PluginSchema, PluginFullSchema, getDefaultBasicPlugin } from "../../../../src/shared/schemas/PluginSchema";
import { sendAppEvent } from "../../Utils";
import classNames from "classnames";

import AdvancedEditor from "./AdvancedEditor";
import usePluginsCategories from "../Plugins/hooks/usePluginCategories";
import { PluginAdmin } from "./PluginAdmin";
import { useToast } from "../ToastWrapper";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { useParams, useLocation, useHistory } from "react-router-dom";
import Textarea from "react-textarea-autosize";
import Auth from "../../Auth";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { Container } from "./styles";
import { PluginTester } from "./PluginTester";
import { AllErrors } from "./AllErrors";
import PluginCreationAccessGate from "./PluginCreationAccessGate";
const EditorModes = {
  basic: "Basic",
  advanced: "Advanced",
};

// Parse the plugin response markdown from Claude
const parsePluginResponse = (result) => {
  const markdown = result.result;

  // Extract title/name
  const titleMatch = markdown.match(/### Title\s*\n([^\n]+)/);
  const name = titleMatch ? titleMatch[1].trim() : "";

  // Extract description
  const descriptionMatch = markdown.match(/### Description\s*\n([\s\S]*?)(?=\n### |$)/);
  const description = descriptionMatch ? descriptionMatch[1].trim() : "";

  // Extract system prompt
  const systemMatch = markdown.match(/### System\s*\n([\s\S]*?)(?=\n### |$)/);
  const system = systemMatch ? systemMatch[1].trim() : "";

  // Extract user prompt
  const userMatch = markdown.match(/### User\s*\n([\s\S]*?)(?=\n### |$)/);
  const user = userMatch ? userMatch[1].trim() : "";

  // Extract category
  const categoryMatch = markdown.match(/### Category\s*\n([^\n]+)/);
  const categoryName = categoryMatch ? categoryMatch[1].trim() : "";

  return { name, description, system, user, categoryName };
};

export default function EditPlugin({ user, subscription }) {
  const [initialValues, setInitialValues] = useState(getDefaultBasicPlugin()); // [getDefaultBasicPlugin()
  const [editorMode, setEditorMode] = useState(EditorModes.basic);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentStepId, setCurrentStepId] = useState("describePlugin");
  const [freeNavigation, setFreeNavigation] = useState(false);
  const { can, loading: adminAccessLoading } = useAdminAccess();
  const canEditOthers = can("admin.tools");
  const { pluginCategories, isLoading } = usePluginsCategories();
  const { setToast } = useToast();

  const [showAdminWarning, setShowAdminWarning] = useState(false);
  const [testSectionOpen, setTestSectionOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const fetchInstalledPlugins = useSudowriteStore((state) => state.fetchInstalledPlugins);
  const subscriptionJSON = useSudowriteStore((state) => state.subscriptionJSON);
  const params = useParams();
  const { id } = params;
  const magicOnly = subscriptionJSON?.hasAccessToManualPluginCreation === false;
  const pluginCreationBlocked = subscriptionJSON?.hasAccessToPluginCreation === false || (Boolean(id) && magicOnly);
  const confirmAction = useConfirmation(id);
  // form the category options
  let categories = null;
  if (!isLoading && pluginCategories) {
    categories = pluginCategories.map((category) => {
      return {
        value: category.id,
        label: category.name,
      };
    });
  }

  useEffect(() => {
    if (pluginCreationBlocked) {
      setIsEdit(Boolean(id));
      return;
    }
    if (id && adminAccessLoading) return;
    let active = true;

    if (id) {
      // this is an edit page
      setIsEdit(true);
      Auth.fetchWithIdToken(`/api/plugins/${id}`, {}).then((response) => {
        // Ignore a fetch that started for another identity or capability state.
        if (!active) return;
        const plugin = response.plugin;
        if (plugin.userId !== user.uid && !canEditOthers) {
          history.push("/app/plugins/");
          return;
        }
        setShowAdminWarning(plugin.userId !== user.uid);
        setInitialValues(plugin);
        // When editing, go directly to the review page
        setCurrentStepId("reviewPlugin");
        setFreeNavigation(true);
      });

      sendAppEvent("Plugins - Edit View", { pluginId: id });
    } else {
      sendAppEvent("Plugins - Create View");
    }
    return () => { active = false; };
  }, [id, pluginCreationBlocked, adminAccessLoading, canEditOthers, user.uid, history]);

  const updatePlugin = async (plugin) => {
    try {
      const params = {
        method: "PUT",
        ...plugin,
      };

      setLoading(true);

      await Auth.fetchWithIdToken(`/api/plugins/${id}`, params);

      setLoading(false);
      // Stay on current step after successful update
      sendAppEvent("Plugins - Updated", { editorMode });
      setToast("Plugin saved successfully");
    } catch (error) {
      const message = (await error.response.json()).error;
      setToast("Error creating plugin: " + message);
      setLoading(false);
    }
  };

  const createPlugin = async (plugin, setFieldValue, values) => {
    try {
      const params = {
        method: "POST",
        ...plugin,
      };

      setLoading(true);

      if (magicOnly) {
        const result = await Auth.fetchWithIdToken("/api/ai/generateOnboardingPlugin", {
          description: plugin.description,
        });
        await fetchInstalledPlugins();
        history.push(`/app/plugin/${result.plugin.shortId}`);
        setLoading(false);
        setToast("Plugin created successfully");
        sendAppEvent("Plugins - Created", { editorMode: "magic" });
        return;
      }

      const result = await Auth.fetchWithIdToken("/api/ai/generatePlugin", params);

      // Parse the response to extract fields
      const parsedFields = parsePluginResponse(result);

      // Prefill the form with the parsed data
      if (parsedFields.name) {
        setFieldValue("name", parsedFields.name);
      }
      if (parsedFields.description) {
        setFieldValue("description", parsedFields.description);
      }

      // Handle category selection
      if (parsedFields.categoryName && categories) {
        const matchingCategory = categories.find((cat) => cat.label.toLowerCase() === parsedFields.categoryName.toLowerCase());
        if (matchingCategory) {
          setFieldValue("categoryId", matchingCategory.value);
        }
      }

      if (parsedFields.system || parsedFields.user) {
        // Update the first inference block with the parsed prompts
        const firstInferenceBlock = values.blocks.find((b) => b.blockType === "inference");
        if (firstInferenceBlock) {
          const blockIndex = values.blocks.findIndex((b) => b.id === firstInferenceBlock.id);

          if (parsedFields.system) {
            setFieldValue(`blocks[${blockIndex}].options.prompt.messages[0].template`, parsedFields.system);
          }
          if (parsedFields.user) {
            setFieldValue(`blocks[${blockIndex}].options.prompt.messages[1].template`, parsedFields.user);
          }
        }
      }

      await fetchInstalledPlugins();

      setCurrentStepId("reviewPlugin");
      setFreeNavigation(true); // unlock free navigation after describe step completes
      setLoading(false);
      setToast("Plugin generated successfully");
      sendAppEvent("Plugins - Generated", { editorMode });
    } catch (error) {
      const json = await error.response?.json();
      if (json) {
        const message = json.error;
        setToast("Error generating plugin: " + message);
      } else {
        setToast("Error generating plugin.");
      }
      setLoading(false);
    }
  };

  const saveNewPlugin = async (plugin) => {
    try {
      const params = {
        method: "POST",
        ...plugin,
        categoryId: plugin.categoryId || null,
      };

      setLoading(true);

      const result = await Auth.fetchWithIdToken("/api/plugins", params);
      await fetchInstalledPlugins();

      setLoading(false);
      history.push(`/app/plugin/${result.plugin.shortId}`);

      setToast("Plugin created successfully");
      sendAppEvent("Plugins - Created", { editorMode });
    } catch (error) {
      const json = await error.response.json();
      if (json) {
        const message = json.error;
        setToast("Error saving plugin: " + message);
      } else {
        setToast("Error saving plugin.");
      }
      setLoading(false);
    }
  };

  const deletePlugin = async e => {
    e.preventDefault();
    await confirmAction({
      title: "Delete plugin",
      message: "Are you sure you want to delete this plugin?",
      actionLabel: "Delete plugin",
      destructive: true
    }, async () => {
      try {
        const params = {
          method: "DELETE"
        };
        setLoading(true);
        await Auth.fetchWithIdToken(`/api/plugins/${id}`, params);
        setLoading(false);
        fetchInstalledPlugins();
        history.push(`/app/plugins/`);
        sendAppEvent("Plugins - Deleted");
        setToast("Plugin deleted successfully");
      } catch (error) {
        const message = (await error.response.json()).error;
        setToast("Error deleting plugin: " + message);
        setLoading(false);
      }
    });
  };

  const handleSkipAhead = () => {
    setCurrentStepId("reviewPlugin");
    setFreeNavigation(true);
    sendAppEvent("Plugins - Skipped Ahead");
  };

  if (id && !initialValues.name && !pluginCreationBlocked) {
    // we are fetching;
    return null;
  }

  // user_text_input is auto-managed based on prompt usage, so we don't add
  // extra front-end-only prompt errors for that variable anymore.
  const validateTemplateVariables = () => ({});

  const handleBackButton = () => {
    if (currentStepId === "reviewPlugin") {
      // Go back to Step 1
      setCurrentStepId("describePlugin");
      sendAppEvent("Plugins - Back to Step 1");
    } else {
      // Use origin state if available, fall back to plugins list
      history.push(location.state?.from || "/app/plugins");
    }
  };

  return (
    <Container className="edit-plugin">
      <div className="header">
        <div className="header-left">
          <SudoButton
            variant="secondaryGrey"
            type="button"
            onClick={handleBackButton}
            leftIcon="/assets/back-arrow.svg"
          >
            {currentStepId === "reviewPlugin" ? "Back to Step 1" : (location.state?.from ? "Back" : "Back to Plugins")}
          </SudoButton>
        </div>
        <div className="header-center">
          <InlineSVG
            src="/assets/plugin.svg"
            className="inline-block w-8 h-8 mr-2 [&_path]:fill-app-grey-0"
          />
          <h1 className="inline-block editPluginTitle">
            {isEdit ? "Edit" : "Create"} Plugin
          </h1>
        </div>
        <div className="header-right">
          <SudoButton
            variant="secondaryGrey"
            as="a"
            target="_blank"
            href="https://docs.sudowrite.com/using-sudowrite/1ow1qkGqof9rtcyGnrWUBS/what-are-plugins/nv29Kiu2qRSNsMgG4hvL75"
            leftIcon="/assets/export.svg"
          >
            Plugin Guide
          </SudoButton>
        </div>
      </div>

      {showAdminWarning && <div className="admin-warning">WARNING: You are editing a user's plug-in that is not yours. Be careful!</div>}

      <PluginCreationAccessGate blocked={pluginCreationBlocked}>
        <Formik
          initialValues={initialValues}
          validationSchema={PluginSchema}
          validate={validateTemplateVariables}
          enableReinitialize={false}
          onSubmit={(values, { setFieldValue }) => {
            if (isEdit) {
              updatePlugin(values);
            } else {
              createPlugin(values, setFieldValue, values);
            }
          }}
        >
          {({ errors, values, setErrors, setTouched }) => {
          return (
            <Form className="edit-plugin-form">
              <div className="formContainer">
                {currentStepId === "describePlugin" && (
                  <>
                    <Field name="description">
                      {({ field, meta }) => (
                        <label htmlFor="edit-plugin-step-one-description" className="step-one-field">
                          <h3 id="edit-plugin-step-one-description-label" className="step-one-title">Step 1: Describe what your plugin should do</h3>
                          <p id="edit-plugin-step-one-description-hint" className="step-one-hint">For best results, include an example</p>
                          <Textarea
                            id="edit-plugin-step-one-description"
                            className="step-one-textarea"
                            error={meta.error && meta.touched}
                            aria-labelledby="edit-plugin-step-one-description-label"
                            aria-describedby="edit-plugin-step-one-description-hint"
                            aria-invalid={Boolean(meta.error && meta.touched)}
                            placeholder={`This plugin simulates reader reactions to a chunk of text. The writer must highlight text before using the plugin.

example
highlighted text: "... just as Josiah grasped Eleanor's hand, she felt a spike of electricity shoot up her spine"
results: Reader 1: "spike of electricity? really? how cliche...`}
                            {...field}
                          />
                        </label>
                      )}
                    </Field>

                    {errors.description && <p className="error">{errors.description}</p>}
                  </>
                )}

                {currentStepId === "reviewPlugin" && !magicOnly && (
                  <>
                    <div className="step-two-heading">
                      <h3>Step 2: Review your plugin</h3>
                      <p>Make any changes you like</p>
                    </div>
                    <AdvancedEditor categories={categories} />
                    <div className="mt-8">
                      <SudoButton
                        type="button"
                        variant="tertiaryGrey"
                        className={classNames("test-section-toggle", {
                          "is-open": testSectionOpen,
                        })}
                        leftIcon="/assets/settings_dropdown_icon.svg"
                        iconSize={14}
                        aria-expanded={testSectionOpen}
                        aria-controls="plugin-testing-section"
                        onClick={(e) => {
                          e.preventDefault();
                          setTestSectionOpen(!testSectionOpen);
                        }}
                      >
                        Test Your Plugin (optional)
                      </SudoButton>
                      <AnimatePresence initial={false}>
                        {testSectionOpen && (
                          <motion.div
                            id="plugin-testing-section"
                            className="test-section-motion"
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <div className="test-section-container">
                              <PluginTester />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </>
                )}

                <div className={`footer ${isEdit && currentStepId === "reviewPlugin" ? "mt-8" : "mt-4"}`}>
                  <div className="left-side">
                    {currentStepId === "reviewPlugin" && !magicOnly && (
                      <div className="flex items-center gap-3">
                        {isEdit && (
                          <button
                            className="deletePluginButton"
                            onClick={deletePlugin}
                            disabled={loading}
                          >
                            <InlineSVG src="/assets/delete.svg" />
                            <p>Delete Plugin...</p>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="right-side">
                    {currentStepId === "reviewPlugin" && !magicOnly && (
                      <div className="flex items-center gap-3">
                        <SudoButton
                          variant="primary"
                          state={loading ? "loading" : "default"}
                          loadingPercentage={loading ? 90 : undefined}
                          loadingTransitionDuration={2}
                          type="button"
                          disabled={loading || Object.keys(errors).length > 0}
                          onClick={async () => {
                            try {
                              await PluginFullSchema.validate(values, { abortEarly: false });
                            } catch (err) {
                              if (err && err.name === "ValidationError") {
                                let nestedErrors = {};
                                let nestedTouched = {};
                                err.inner?.forEach((e) => {
                                  if (!e?.path) return;
                                  // Only set the first error per path and ensure nesting so ErrorMessage picks it up
                                  const alreadySet = getIn(nestedErrors, e.path);
                                  if (!alreadySet) {
                                    nestedErrors = setIn(nestedErrors, e.path, e.message);
                                    nestedTouched = setIn(nestedTouched, e.path, true);
                                  }
                                });
                                setErrors(nestedErrors);
                                setTouched(nestedTouched, false);
                                const isMissingUserInstructionsError = err.inner?.some(
                                  (e) =>
                                    e?.type === "userTextInputRequiresCustomizedInstructions"
                                    || e?.message?.includes("custom popup instructions for User Instructions")
                                );
                                setToast(
                                  isMissingUserInstructionsError
                                    ? "Please customize user instructions before saving."
                                    : "Please fix the highlighted errors before saving"
                                );
                                return;
                              }
                            }

                            if (isEdit) {
                              updatePlugin(values);
                            } else {
                              saveNewPlugin(values);
                            }
                          }}
                        >
                          Save Plugin
                        </SudoButton>
                      </div>
                    )}
                    {!isEdit && currentStepId === "describePlugin" && (
                      <div className="flex items-center gap-3">
                        {!magicOnly && <SudoButton
                          variant="tertiary"
                          type="button"
                          onClick={handleSkipAhead}
                          disabled={loading}
                        >
                          Skip
                        </SudoButton>}
                        <SudoButton
                          variant="primary"
                          state={loading ? "loading" : "default"}
                          loadingPercentage={loading ? 90 : undefined}
                          leftIcon="/assets/AI-sparkle.svg"
                          loadingTransitionDuration={2}
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Generating Plugin..." : "Generate Plugin..."}
                        </SudoButton>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <AllErrors />
              {can("admin.tools") && <PluginAdmin pluginId={id} />}
            </Form>
          );
          }}
        </Formik>
      </PluginCreationAccessGate>
    </Container>
  );
}
